import { Link } from '@remix-run/react'

import { allLegalPages } from './templates'

/** Brand identity declared in one place so every marketing surface tells the
 *  same story:
 *  - "Quiver Labs" is the company / legal entity (used in contracts, EULAs,
 *    licensing applications)
 *  - "QuiverOS" is the product (the operating system itself)
 *  - quiveros.ai is the public marketing domain
 */
export const BRAND = {
  org: 'Quiver Labs',
  product: 'QuiverOS',
  marketingName: 'QuiverOS',
  domain: 'quiveros.ai',
  /** The company's own site (the workshop behind the product). */
  orgUrl: 'https://quiverlabs.ai',
} as const

/**
 * Footer for every public/marketing surface (landing page, template detail
 * pages, legal pages). Declares the org → product relationship and lists every
 * legal page automatically — adding a new legal page to a template manifest
 * surfaces it here for free.
 */
export function MarketingFooter() {
  const legal = allLegalPages()
  const year = new Date().getFullYear()
  return (
    <footer className="relative left-1/2 w-screen -translate-x-1/2 border-t border-white/10 bg-white/[0.02]">
      <div className="mx-auto max-w-6xl px-6 py-8 sm:py-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-zinc-50">
              {BRAND.product}
            </p>
            <p className="max-w-md text-xs text-zinc-500">
              An AI-native operating system. Built and operated by{' '}
              <a
                href={BRAND.orgUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-zinc-300 underline decoration-white/20 underline-offset-2 transition-colors hover:text-indigo-300 hover:decoration-indigo-300"
              >
                {BRAND.org}
              </a>
              .
            </p>
          </div>

          <div className="flex gap-12">
            <nav aria-label="Product" className="text-xs">
              <p className="font-semibold uppercase tracking-wider text-zinc-500">
                Product
              </p>
              <ul className="mt-2 space-y-1">
                {[
                  { to: '/apps', label: 'Apps' },
                  { to: '/download', label: 'Download' },
                  { to: '/docs', label: 'Docs' },
                ].map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="text-zinc-400 transition-colors hover:text-indigo-300"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {legal.length > 0 ? (
              <nav aria-label="Legal" className="text-xs">
                <p className="font-semibold uppercase tracking-wider text-zinc-500">
                  Legal
                </p>
                <ul className="mt-2 space-y-1">
                  {legal.map(({ page }) => (
                    <li key={page.slug}>
                      <Link
                        to={`/legal/${page.slug}`}
                        className="text-zinc-400 transition-colors hover:text-indigo-300"
                      >
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
        </div>

        <p className="mt-8 text-xs text-zinc-500">
          © {year}{' '}
          <a
            href={BRAND.orgUrl}
            target="_blank"
            rel="noreferrer"
            className="transition-colors hover:text-indigo-300"
          >
            {BRAND.org}
          </a>{' '}
          · {BRAND.domain}
        </p>
      </div>
    </footer>
  )
}

export default MarketingFooter
